import httpService from '../../../core/services/HttpService';
import type {
  Applicant,
  ApplicantCreateData,
  ApplicantFilters,
  ApplicantNote,
  ApplicantNoteCreateData,
  ApplicantStatus,
  ApplicantTriage,
  EvaluationPhase,
  StageEvaluation,
  StageEvaluationSubmitData,
} from '../types';

const BASE_URL = '/api/admin/recruiting';

export default class RecruitingService {
  static async fetchApplicants(filters?: ApplicantFilters): Promise<Applicant[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.team_choice) params.append('team_choice', filters.team_choice);
    if (filters?.desired_employment_type) params.append('desired_employment_type', filters.desired_employment_type);
    if (filters?.triage) params.append('triage', filters.triage);

    const query = params.toString();
    const url = `${BASE_URL}/applicants/${query ? `?${query}` : ''}`;
    const response = await httpService.get<Applicant[] | { results: Applicant[] }>(url);
    const data = response.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'results' in data) return data.results;
    return [];
  }

  static async getApplicant(id: number): Promise<Applicant> {
    const response = await httpService.get<Applicant>(`${BASE_URL}/applicants/${id}/`);
    return response.data;
  }

  static async createApplicant(data: ApplicantCreateData): Promise<Applicant> {
    const response = await httpService.post<Applicant>(`${BASE_URL}/applicants/`, data);
    return response.data;
  }

  static async updateApplicant(id: number, data: Partial<ApplicantCreateData>): Promise<Applicant> {
    const response = await httpService.patch<Applicant>(`${BASE_URL}/applicants/${id}/`, data);
    return response.data;
  }

  static async changeStatus(id: number, status: ApplicantStatus): Promise<Applicant> {
    const response = await httpService.post<Applicant>(`${BASE_URL}/applicants/${id}/status/`, { status });
    return response.data;
  }

  static async setTriage(id: number, triage: ApplicantTriage): Promise<Applicant> {
    const response = await httpService.patch<Applicant>(`${BASE_URL}/applicants/${id}/`, { triage });
    return response.data;
  }

  static async convertToEmployee(id: number): Promise<{ employee_id: number }> {
    const response = await httpService.post<{ employee_id: number }>(`${BASE_URL}/applicants/${id}/convert/`);
    return response.data;
  }

  // --- Pipeline tracking (Phase 6.4) ---

  static async advancePipelineStep(id: number, outcome?: string, notes?: string): Promise<Applicant> {
    const response = await httpService.post<Applicant>(
      `${BASE_URL}/applicants/${id}/advance/`,
      { outcome, notes }
    );
    return response.data;
  }

  static async revertPipelineStep(id: number, notes?: string): Promise<Applicant> {
    const response = await httpService.post<Applicant>(
      `${BASE_URL}/applicants/${id}/revert/`,
      { notes }
    );
    return response.data;
  }

  static async fetchEvaluations(applicantId: number, phase?: EvaluationPhase): Promise<StageEvaluation[]> {
    const params = phase ? `?phase=${phase}` : '';
    const response = await httpService.get<StageEvaluation[]>(
      `${BASE_URL}/applicants/${applicantId}/evaluations/${params}`
    );
    return response.data;
  }

  static async createEvaluation(applicantId: number, data: StageEvaluationSubmitData): Promise<StageEvaluation> {
    const response = await httpService.post<StageEvaluation>(
      `${BASE_URL}/applicants/${applicantId}/evaluations/`,
      data,
    );
    return response.data;
  }

  static async updateEvaluation(evaluationId: number, data: Partial<StageEvaluationSubmitData>): Promise<StageEvaluation> {
    const response = await httpService.patch<StageEvaluation>(
      `${BASE_URL}/evaluations/${evaluationId}/`,
      data,
    );
    return response.data;
  }

  static async fetchEvaluationForm(applicantId: number, phase?: EvaluationPhase): Promise<any> {
    const params = phase ? `?phase=${phase}` : '';
    const response = await httpService.get(
      `${BASE_URL}/applicants/${applicantId}/evaluation-form/${params}`
    );
    return response.data;
  }

  static async fetchNotes(applicantId: number): Promise<ApplicantNote[]> {
    const response = await httpService.get<ApplicantNote[]>(
      `${BASE_URL}/applicants/${applicantId}/notes/`
    );
    return response.data;
  }

  static async createNote(applicantId: number, data: ApplicantNoteCreateData): Promise<ApplicantNote> {
    const response = await httpService.post<ApplicantNote>(
      `${BASE_URL}/applicants/${applicantId}/notes/`,
      data,
    );
    return response.data;
  }

  static async deleteNote(noteId: number): Promise<void> {
    await httpService.delete(`${BASE_URL}/notes/${noteId}/`);
  }

  static async uploadCv(id: number, file: File): Promise<Applicant> {
    const formData = new FormData();
    formData.append('cv_file', file);
    const response = await httpService.post<Applicant>(
      `${BASE_URL}/applicants/${id}/upload-cv/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  static async deleteCv(id: number): Promise<Applicant> {
    const response = await httpService.delete<Applicant>(`${BASE_URL}/applicants/${id}/upload-cv/`);
    return response.data;
  }

  static async deleteApplicant(id: number): Promise<void> {
    await httpService.delete(`${BASE_URL}/applicants/${id}/`);
  }
}
